/**
 * Tempo real, parte pura (sem React nem rede, para testar): que tabelas o
 * Supply escuta, como a rajada de mudanças vira UMA recarga por escopo, e
 * quando uma mudança é do registro que a pessoa tem aberto.
 *
 * A conexão e a tela ficam em `realtime.tsx`.
 */

/** Mesma lista das migrations 20261022170000_tempo_real.sql e 20261022210000_configuracoes.sql (e do teste SQL 008). */
export const LIVE_TABLES = [
  'task',
  'task_action',
  'task_event',
  'task_comment',
  'q3_decision',
  'ata',
  'ata_participant',
  'ata_local',
  'notification',
  'area',
  'area_member',
  'member',
  'member_role',
  'performance_target',
  'setting',
  // Contadores da barra (SIS-508): marcar rotina atualiza o badge de /rotinas.
  'routine',
  'routine_check',
] as const

export type LiveTable = (typeof LIVE_TABLES)[number]

export interface LiveChange {
  readonly table: LiveTable
  readonly eventType: 'INSERT' | 'UPDATE' | 'DELETE'
  /** A linha nova (vazia no DELETE). */
  readonly row: Readonly<Record<string, unknown>>
  /** A linha antiga: com RLS, o Realtime manda só a chave. */
  readonly old: Readonly<Record<string, unknown>>
}

/**
 * `referencia`: áreas, pessoas, papéis, metas e configurações — moram na sessão
 * (`reloadReference`), que recarrega as telas junto. `dados`: o resto, que
 * cada tela lê com `useLoad` (`refreshAll`).
 */
export type LiveScope = 'referencia' | 'dados'

const REFERENCE_TABLES: ReadonlySet<LiveTable> = new Set<LiveTable>([
  'area',
  'area_member',
  'member',
  'member_role',
  'performance_target',
  'setting',
])

export function scopeOf(table: LiveTable): LiveScope {
  return REFERENCE_TABLES.has(table) ? 'referencia' : 'dados'
}

export interface LiveRouterOptions {
  /** Recarrega o escopo; recebe as mudanças agrupadas. */
  readonly flush: (scope: LiveScope, changes: readonly LiveChange[]) => void
  /** Espera depois da última mudança da rajada (padrão 300 ms). */
  readonly delayMs?: number
  /** Teto da espera numa rajada contínua (padrão 2 s). */
  readonly maxWaitMs?: number
  /** `true` = alguém está editando: segura a recarga e confere de novo depois. */
  readonly hold?: () => boolean
  /** Intervalo de nova conferência enquanto segura (padrão 500 ms). */
  readonly retryMs?: number
}

export interface LiveRouter {
  push(change: LiveChange): void
  /** Confere agora o que está esperando (a edição pode ter acabado). */
  kick(): void
  /** Recarrega já, mesmo com edição em curso (a pessoa pediu). */
  force(): void
  /** Há recarga esperando? */
  pending(): boolean
  dispose(): void
}

interface Pending {
  changes: LiveChange[]
  first: number
  timer: ReturnType<typeof setTimeout> | null
}

export function createLiveRouter(options: LiveRouterOptions): LiveRouter {
  const delayMs = options.delayMs ?? 300
  const maxWaitMs = options.maxWaitMs ?? 2_000
  const retryMs = options.retryMs ?? 500
  const pending = new Map<LiveScope, Pending>()

  const clear = (p: Pending) => {
    if (p.timer !== null) clearTimeout(p.timer)
    p.timer = null
  }

  const flush = (scope: LiveScope) => {
    const p = pending.get(scope)
    if (!p) return
    clear(p)
    pending.delete(scope)
    let changes = p.changes
    // A sessão recarrega as telas junto: `dados` esperando vai no mesmo pacote.
    const dados = scope === 'referencia' ? pending.get('dados') : undefined
    if (dados) {
      clear(dados)
      pending.delete('dados')
      changes = [...changes, ...dados.changes]
    }
    options.flush(scope, changes)
  }

  const fire = (scope: LiveScope) => {
    const p = pending.get(scope)
    if (!p) return
    p.timer = null
    if (options.hold?.()) {
      p.timer = setTimeout(() => fire(scope), retryMs)
      return
    }
    flush(scope)
  }

  // `referencia` antes: ela leva `dados` junto.
  const scopes = (): LiveScope[] => (['referencia', 'dados'] as const).filter((s) => pending.has(s))

  return {
    push(change) {
      const scope = scopeOf(change.table)
      const now = Date.now()
      let p = pending.get(scope)
      if (!p) {
        p = { changes: [], first: now, timer: null }
        pending.set(scope, p)
      }
      p.changes.push(change)
      clear(p)
      const wait = Math.max(0, Math.min(delayMs, p.first + maxWaitMs - now))
      p.timer = setTimeout(() => fire(scope), wait)
    },
    kick() {
      for (const scope of scopes()) {
        const p = pending.get(scope)
        if (p) clear(p)
        fire(scope)
      }
    },
    force() {
      for (const scope of scopes()) flush(scope)
    },
    pending: () => pending.size > 0,
    dispose() {
      for (const p of pending.values()) clear(p)
      pending.clear()
    },
  }
}

// ---------------------------------------------------------------------------
// Registro aberto: a tarefa do painel (`?tarefa=SUP-12`) ou a ATA da reunião
// ---------------------------------------------------------------------------

export type OpenRecord =
  | { readonly kind: 'tarefa'; readonly key: string; readonly id: string | null }
  | { readonly kind: 'ata'; readonly tipo: 'q3' | 'ras'; readonly semana: string; readonly areaKey: string }

/** Mesmo valor de `GERAL` das telas de reunião: ATA sem área. */
export const ATA_GERAL = 'geral'

const ATA_ROUTES: Readonly<Record<string, 'q3' | 'ras'>> = { '/reuniao': 'q3', '/reuniao-ras': 'ras' }

/**
 * O que a pessoa tem aberto, lido da URL do jeito que as telas leem: semana
 * inválida ou ausente = semana atual; área ausente = a padrão da pessoa.
 */
export function openRecordFromLocation(
  pathname: string,
  search: string,
  defaults: { readonly week: string; readonly areaKey: string; readonly isWeek: (value: string) => boolean },
): OpenRecord | null {
  const params = new URLSearchParams(search)
  const key = params.get('tarefa')
  if (key) return { kind: 'tarefa', key: key.toUpperCase(), id: null }
  const tipo = ATA_ROUTES[pathname]
  if (!tipo) return null
  const semana = params.get('semana')
  return {
    kind: 'ata',
    tipo,
    semana: semana && defaults.isWeek(semana) ? semana : defaults.week,
    areaKey: params.get('area') ?? defaults.areaKey,
  }
}

/** Chave da presença: quem tem a mesma chave está vendo a mesma coisa. */
export function viewKeyOf(open: OpenRecord | null): string | null {
  if (!open) return null
  return open.kind === 'tarefa' ? `tarefa:${open.key}` : `ata:${open.tipo}:${open.semana}:${open.areaKey}`
}

const TASK_CHILDREN: ReadonlySet<LiveTable> = new Set<LiveTable>(['task_action', 'task_event', 'task_comment', 'q3_decision'])

export function touchesOpenRecord(change: LiveChange, open: OpenRecord | null): boolean {
  if (!open) return false
  const { row, old } = change
  if (open.kind === 'tarefa') {
    if (change.table === 'task') {
      return row.key === open.key || (open.id !== null && (row.id === open.id || old.id === open.id))
    }
    return open.id !== null && TASK_CHILDREN.has(change.table) && row.task_id === open.id
  }
  if (change.table !== 'ata') return false
  return row.tipo === open.tipo && row.semana === open.semana && ((row.area_id as string | null) ?? ATA_GERAL) === open.areaKey
}

/**
 * Quem fez a mudança, quando a linha diz: `updated_by` (tarefa), `actor_id`
 * (histórico) ou `author_id` (comentário). Sem coluna de autoria, `null` — o
 * tempo real compara com a última escrita própria.
 */
export function actorOf(change: LiveChange): string | null {
  const { row } = change
  for (const column of ['updated_by', 'actor_id', 'author_id'] as const) {
    const value = row[column]
    if (typeof value === 'string' && value) return value
  }
  return null
}
